export interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePermissionRequest {
  name: string;
  resource: string;
  action: string;
  description?: string;
}

export interface UpdatePermissionRequest {
  id: number;
  description?: string;
}

export interface PermissionFilters {
  resource?: string;
  action?: string;
  page?: number;
  limit?: number;
}

export interface RolePermissionAssignment {
  id: number;
  roleId: number;
  permissionId: number;
  grantedBy: number;
  grantedAt: string;
  permission: Permission;
}

export interface UserPermissionOverride {
  id: number;
  userId: number;
  permissionId: number;
  grantedBy: number;
  grantedAt: string;
  expiresAt?: string;
  reason?: string;
  permission: Permission;
}

export interface AssignRolePermissionRequest {
  roleId: number;
  permissionId: number;
  grantedBy: number;
}

export interface BulkAssignRolePermissionsRequest {
  roleId: number;
  permissionIds: number[];
  grantedBy: number;
}

export interface BulkRemoveRolePermissionsRequest {
  roleId: number;
  permissionIds: number[];
  reason?: string;
}

export interface AssignUserPermissionRequest {
  userId: number;
  permissionId: number;
  grantedBy: number;
  expiresAt?: string;
  reason?: string;
}

export interface DenyUserPermissionRequest {
  userId: number;
  permissionId: number;
  grantedBy: number;
  reason?: string;
}

export interface UserEffectivePermissions {
  userId: number;
  rolePermissions: Permission[];
  userOverrides: {
    granted: Permission[];
    denied: Permission[];
  };
  effectivePermissions: Permission[];
}

// Common resource types for the system
export enum ResourceType {
  USERS = 'users',
  STORES = 'stores',
  PRODUCTS = 'products',
  INVENTORY = 'inventory',
  SALES = 'sales',
  DISTRIBUTION = 'distribution',
  VISITS = 'visits',
  BILLING = 'billing',
  REPORTS = 'reports',
  SETTINGS = 'settings',
  PERMISSIONS = 'permissions',
  ROLES = 'roles',
  BRANDS = 'brands',
  DASHBOARD = 'dashboard',
  MOBILE = 'mobile',
  SYSTEM = 'system'
}

// Common action types
export enum ActionType {
  // Core CRUD
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',

  // Management
  MANAGE = 'manage',
  ASSIGN = 'assign',

  // Business Actions
  APPROVE = 'approve',
  EXPORT = 'export',
  TRANSFER = 'transfer',
  COLLECT_PAYMENT = 'collect_payment',

  // System Actions
  ACCESS = 'access',
  ANALYTICS = 'analytics',
  REPORTS = 'reports',
  SETTINGS = 'settings',
  LOGS = 'logs',
  BACKUP = 'backup',

  // Domain Specific
  SALES = 'sales',
  VISITS = 'visits',

  // Legacy (keep for backward compatibility)
  LIST = 'list',
  IMPORT = 'import',
  REJECT = 'reject'
}

// Predefined permissions for the system
export const SYSTEM_PERMISSIONS = {
  // User Management
  USER_CREATE: {name: 'user_create', resource: ResourceType.USERS, action: ActionType.CREATE},
  USER_READ: {name: 'user_read', resource: ResourceType.USERS, action: ActionType.READ},
  USER_UPDATE: {name: 'user_update', resource: ResourceType.USERS, action: ActionType.UPDATE},
  USER_DELETE: {name: 'user_delete', resource: ResourceType.USERS, action: ActionType.DELETE},
  USER_LIST: {name: 'user_list', resource: ResourceType.USERS, action: ActionType.LIST},
  USER_MANAGE: {name: 'user_manage', resource: ResourceType.USERS, action: ActionType.MANAGE},

  // Store Management
  STORE_CREATE: {name: 'store_create', resource: ResourceType.STORES, action: ActionType.CREATE},
  STORE_READ: {name: 'store_read', resource: ResourceType.STORES, action: ActionType.READ},
  STORE_UPDATE: {name: 'store_update', resource: ResourceType.STORES, action: ActionType.UPDATE},
  STORE_DELETE: {name: 'store_delete', resource: ResourceType.STORES, action: ActionType.DELETE},
  STORE_LIST: {name: 'store_list', resource: ResourceType.STORES, action: ActionType.LIST},
  STORE_MANAGE: {name: 'store_manage', resource: ResourceType.STORES, action: ActionType.MANAGE},

  // Product Management
  PRODUCT_CREATE: {name: 'product_create', resource: ResourceType.PRODUCTS, action: ActionType.CREATE},
  PRODUCT_READ: {name: 'product_read', resource: ResourceType.PRODUCTS, action: ActionType.READ},
  PRODUCT_UPDATE: {name: 'product_update', resource: ResourceType.PRODUCTS, action: ActionType.UPDATE},
  PRODUCT_DELETE: {name: 'product_delete', resource: ResourceType.PRODUCTS, action: ActionType.DELETE},
  PRODUCT_LIST: {name: 'product_list', resource: ResourceType.PRODUCTS, action: ActionType.LIST},
  PRODUCT_MANAGE: {name: 'product_manage', resource: ResourceType.PRODUCTS, action: ActionType.MANAGE},

  // Permission Management
  PERMISSION_CREATE: {name: 'permission_create', resource: ResourceType.PERMISSIONS, action: ActionType.CREATE},
  PERMISSION_READ: {name: 'permission_read', resource: ResourceType.PERMISSIONS, action: ActionType.READ},
  PERMISSION_UPDATE: {name: 'permission_update', resource: ResourceType.PERMISSIONS, action: ActionType.UPDATE},
  PERMISSION_DELETE: {name: 'permission_delete', resource: ResourceType.PERMISSIONS, action: ActionType.DELETE},
  PERMISSION_LIST: {name: 'permission_list', resource: ResourceType.PERMISSIONS, action: ActionType.LIST},
  PERMISSION_MANAGE: {name: 'permission_manage', resource: ResourceType.PERMISSIONS, action: ActionType.MANAGE},

  // Role Management
  ROLE_CREATE: {name: 'role_create', resource: ResourceType.ROLES, action: ActionType.CREATE},
  ROLE_READ: {name: 'role_read', resource: ResourceType.ROLES, action: ActionType.READ},
  ROLE_UPDATE: {name: 'role_update', resource: ResourceType.ROLES, action: ActionType.UPDATE},
  ROLE_DELETE: {name: 'role_delete', resource: ResourceType.ROLES, action: ActionType.DELETE},
  ROLE_LIST: {name: 'role_list', resource: ResourceType.ROLES, action: ActionType.LIST},
  ROLE_MANAGE: {name: 'role_manage', resource: ResourceType.ROLES, action: ActionType.MANAGE}
};
